import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Info, XCircle, Loader2 } from "lucide-react";
import apiClient from "../../api/apiClient";
import "./VerifyEmailPage.css";
import MobileTopBar from "../../components/MobileTopBar";
import DesktopTopBar from "../../components/DesktopTopBar";

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // 'loading', 'success', 'already_verified', 'error'
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStatus("error");
        setErrorMessage("No verification token found.");
        return;
      }

      try {
        const response = await apiClient.get(
          `/emailVerification/verify-email?token=${token}`,
        );

        // Handle both plain text strings and JSON objects
        const responseData = response.data;
        const responseText =
          typeof responseData === "string" ? responseData.toLowerCase() : "";
        const messageText = responseData?.message?.toLowerCase() || "";

        if (
          responseText.includes("already verified") ||
          messageText.includes("already verified") ||
          response.status === 208
        ) {
          setStatus("already_verified");
        }
        // Then handle standard success
        else if (
          response.status === 200 ||
          responseData?.status === true ||
          responseText.includes("successfully")
        ) {
          setStatus("success");
        }
        // Fallback error
        else {
          setStatus("error");
          setErrorMessage(
            "We couldn't verify your email. The link might be invalid or expired.",
          );
        }
      } catch (error) {
        const errorData = error.response?.data;
        const errorText =
          typeof errorData === "string" ? errorData.toLowerCase() : "";
        const errorMessageText = errorData?.message?.toLowerCase() || "";

        if (
          errorText.includes("already verified") ||
          errorMessageText.includes("already verified")
        ) {
          setStatus("already_verified");
        } else {
          setStatus("error");
          setErrorMessage(
            errorData?.message ||
              errorData ||
              "An error occurred during verification. Please try again.",
          );
        }
      }
    };

    verifyToken();
  }, [token]);

  useEffect(() => {
    if (status === "success" || status === "already_verified") {
      const channel = new BroadcastChannel("sp_verification_channel");
      channel.postMessage("email_verified");
      channel.close();
    }
  }, [status]);

  const handleReturnToApp = () => {
    navigate("/onboarding");
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <div className="ve-card">
            <div className="ve-icon-wrapper">
              <Loader2 className="animate-spin" size={56} color="#ffffff" />
            </div>
            <h2 className="ve-title">Verifying...</h2>
            <p className="ve-text">
              Please wait a moment while we confirm your details.
            </p>
          </div>
        );

      case "success":
        return (
          <div className="ve-card">
            <div className="ve-icon-wrapper">
              <CheckCircle size={64} color="#6AD9A4" />
            </div>
            <h2 className="ve-title">Email Verified!</h2>
            <p className="ve-text">
              Please return to Social Paws and hit NEXT to continue creating
              your profile!
            </p>
            <button
              className="ve-btn"
              onClick={handleReturnToApp}
              style={{
                marginTop: "15px",
                padding: "14px 28px",
                backgroundColor: "#6AD9A4",
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              Return to Social Paws
            </button>
          </div>
        );

      case "already_verified":
        return (
          <div className="ve-card">
            <div className="ve-icon-wrapper">
              <Info size={64} color="#A4D9EB" />
            </div>
            <h2 className="ve-title">Already Verified</h2>
            <p className="ve-text">
              This email is already verified. Please return to Social Paws and
              hit NEXT to continue creating your profile!
            </p>
            <button
              className="ve-btn"
              onClick={handleReturnToApp}
              style={{
                marginTop: "20px",
                padding: "14px 28px",
                backgroundColor: "#009FDC",
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              Return to Social Paws
            </button>
          </div>
        );

      case "error":
      default:
        return (
          <div className="ve-card">
            <div className="ve-icon-wrapper">
              <XCircle size={64} color="#ff9999" />
            </div>
            <h2 className="ve-title">Verification Failed</h2>
            <p className="ve-text ve-text-error">{errorMessage}</p>
            <button
              className="ve-btn"
              onClick={handleReturnToApp}
              style={{
                marginTop: "20px",
                padding: "14px 28px",
                backgroundColor: "#000",
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              Return to Social Paws
            </button>
          </div>
        );
    }
  };

  return (
    <div className="ve-container">
      <MobileTopBar
        showBackButton={false}
        backColor="transparent"
        logoColor="#fff"
        isProtectedRoute={false}
      />

      <div className="ve-content-section">{renderContent()}</div>
    </div>
  );
};

export default VerifyEmailPage;
